import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Switch,
  Tooltip,
  Divider,
  FormControlLabel,
} from '@mui/material'
import StarIcon from '@mui/icons-material/Star'
import { supabase } from '../config/supabase'

interface Review {
  id: string
  author: string | null
  rating: number
  text: string | null
  review_date: string | null
  hidden: boolean
  featured: boolean
  language: string | null
}

interface BusinessReviewsListProps {
  undertakerId?: string
  chainId?: string
}

// Compact per-profile review moderation — same hidden/featured flags as /admin/reviews
export default function BusinessReviewsList({ undertakerId, chainId }: BusinessReviewsListProps) {
  const [reviews, setReviews] = useState<Review[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function fetchReviews() {
      setLoading(true)
      try {
        let query = supabase
          .from('business_reviews')
          .select('id, author, rating, text, review_date, hidden, featured, language')
          .order('review_date', { ascending: false })
          .limit(500)
        query = undertakerId ? query.eq('undertaker_id', undertakerId) : query.eq('chain_id', chainId)
        const { data, error } = await query
        if (error) throw error
        setReviews(data || [])
      } catch (err: any) {
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }
    if (undertakerId || chainId) fetchReviews()
    else setLoading(false)
  }, [undertakerId, chainId])

  const toggleFlag = async (review: Review, flag: 'hidden' | 'featured') => {
    const newValue = !review[flag]
    setReviews((prev) => prev.map((r) => (r.id === review.id ? { ...r, [flag]: newValue } : r)))
    const { error } = await supabase
      .from('business_reviews')
      .update({ [flag]: newValue })
      .eq('id', review.id)
    if (error) {
      setReviews((prev) => prev.map((r) => (r.id === review.id ? { ...r, [flag]: !newValue } : r)))
      setError(`Kunne ikke opdatere: ${error.message}`)
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  if (reviews.length === 0) return null

  const hiddenCount = reviews.filter((r) => r.hidden).length

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
        Anmeldelser ({reviews.length})
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Skjulte anmeldelser vises aldrig på sitet, fremhævede prioriteres i citat-sektionen.
        Kan også administreres samlet under Google Anmeldelser.
        {hiddenCount > 0 && ` ${hiddenCount} skjult.`}
      </Typography>
      <Divider sx={{ mb: 2 }} />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {reviews.map((review) => (
          <Box
            key={review.id}
            sx={{
              p: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              opacity: review.hidden ? 0.5 : 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
              <StarIcon sx={{ fontSize: 16, color: '#fbbc04' }} />
              <Typography variant="body2" fontWeight={600}>
                {Number(review.rating).toFixed(0)}
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {review.author || 'Anonym'}
              </Typography>
              {review.review_date && (
                <Typography variant="caption" color="text.secondary">
                  {new Date(review.review_date).toLocaleDateString('da-DK', {
                    day: 'numeric', month: 'short', year: 'numeric',
                  })}
                </Typography>
              )}
              {review.language && review.language !== 'da' && (
                <Tooltip title="Vises ikke på sitet – kun danske anmeldelser synkroniseres">
                  <Chip
                    label={review.language.toUpperCase()}
                    size="small"
                    color="warning"
                    sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600 }}
                  />
                </Tooltip>
              )}
            </Box>
            {review.text && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mb: 1,
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {review.text}
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    color="success"
                    checked={review.featured}
                    onChange={() => toggleFlag(review, 'featured')}
                  />
                }
                label={<Typography variant="body2">Fremhævet</Typography>}
              />
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    color="error"
                    checked={review.hidden}
                    onChange={() => toggleFlag(review, 'hidden')}
                  />
                }
                label={<Typography variant="body2">Skjult</Typography>}
              />
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  )
}
